/**
 * Таймлайн плана лечения — третий вид просмотра (после «Детально» и «Схема»).
 *
 * Шапка карточки общая с другими видами, ниже — полоса этапов
 * (PatientPlanTimelineStepper) и детали выбранного этапа
 * (PatientPlanTimelineStage). Данные собирает planTimelineData.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoTimeOutline } from "react-icons/io5";
import { getGroupTypeLabel } from "../../../i18n/groupTypes";
import { Button } from "../../../components/ui";
import { buildAppointmentTypeMap } from "./planStageLabels";
import { buildTimelineStages } from "./planTimelineData";
import PatientPlanTimelineStepper from "./PatientPlanTimelineStepper";
import PatientPlanTimelineStage from "./PatientPlanTimelineStage";
import "./patientPlanTimeline.css";

export default function PatientPlanTimeline({
    groups = [],
    appointmentTypes = [],
    admissions = {},
    planId,
    planTitle = "",
    viewSwitch = null,
    actions = null,
    onDeletePlan = null,
    onCompleteStage = null,
    busy = false,
    deletePlanLabel = "",
}) {
    const { t, i18n } = useTranslation("patient");

    const typeMap = useMemo(
        () => buildAppointmentTypeMap(appointmentTypes, i18n.language),
        [appointmentTypes, i18n.language],
    );

    const stages = useMemo(
        () =>
            buildTimelineStages({
                groups,
                typeMap,
                admissions,
                language: i18n.language,
                stageFallback: () => "",
                groupLabel: (group) => getGroupTypeLabel(t, group),
            }),
        [groups, typeMap, admissions, i18n.language, t],
    );

    // Выбранный этап: по умолчанию — текущий. При смене плана/данных
    // сбрасываем на актуальный, но не перебиваем ручной выбор пользователя.
    const currentNumber = useMemo(() => {
        const current = stages.find((stage) => stage.state === "current");
        if (current) return current.number;
        return stages.length ? stages[stages.length - 1].number : 0;
    }, [stages]);

    const [selected, setSelected] = useState(currentNumber);

    useEffect(() => {
        setSelected(currentNumber);
    }, [currentNumber, planId]);

    const activeStage =
        stages.find((stage) => stage.number === selected) || stages[0] || null;

    return (
        <div className="page-block patient-plan-card plan-timeline-card">
            <div className="card__head plan-matrix-head plan-card__head">
                <div className="plan-matrix-head__title">
                    <div className="card__title plan-matrix-title">
                        <IoTimeOutline size={19} aria-hidden="true" />
                        {planTitle || t("plan.timeline.title")}
                    </div>
                    <div className="card__sub plan-matrix-sub">
                        {t("plan.timeline.sub")}
                    </div>
                </div>
                <div className="plan-matrix-head__switch">{viewSwitch}</div>
                <div className="plan-matrix-head__actions">{actions}</div>
            </div>

            {stages.length === 0 ? (
                <div className="pd-empty">{t("plan.timeline.noStages")}</div>
            ) : (
                <>
                    <PatientPlanTimelineStepper
                        active={activeStage?.number || 0}
                        onSelect={setSelected}
                        stages={stages}
                    />
                    <PatientPlanTimelineStage
                        busy={busy}
                        onCompleteStage={onCompleteStage}
                        planId={planId}
                        stage={activeStage}
                    />
                </>
            )}

            {onDeletePlan ? (
                <div className="plan-timeline-foot">
                    <Button onClick={onDeletePlan} size="md" variant="danger">
                        {deletePlanLabel || t("plan.buttons.deletePlan")}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
